import { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { getRegions, createRegion, updateRegion, deleteRegion } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Plus, Pencil, Trash2, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';

export default function RegionsPage() {
  const { t, isRTL, language } = useLanguage();
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingRegion, setEditingRegion] = useState(null);
  const [deletingRegion, setDeletingRegion] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name_ar: '',
    name_en: '',
    is_active: true,
  });

  const fetchRegions = async () => {
    try {
      const response = await getRegions();
      setRegions(response.data);
    } catch (error) {
      toast.error(t('error.general'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRegions();
  }, []);

  const handleOpenDialog = (region = null) => {
    if (region) {
      setEditingRegion(region);
      setFormData({
        name_ar: region.name_ar || '',
        name_en: region.name_en || '',
        is_active: region.is_active !== false,
      });
    } else {
      setEditingRegion(null);
      setFormData({ name_ar: '', name_en: '', is_active: true });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name_ar.trim()) {
      toast.error(t('validation.required'));
      return;
    }
    
    setSaving(true);
    try {
      if (editingRegion) {
        await updateRegion(editingRegion.id, formData);
        toast.success(t('success.updated'));
      } else {
        await createRegion(formData);
        toast.success(t('success.created'));
      }
      setDialogOpen(false);
      fetchRegions();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error.general'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingRegion) return;
    
    try {
      await deleteRegion(deletingRegion.id);
      toast.success(t('success.deleted'));
      setDeleteDialogOpen(false);
      setDeletingRegion(null);
      fetchRegions();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error.delete_has_children'));
    }
  };

  const filteredRegions = regions.filter(region => 
    region.name_ar?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    region.name_en?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#0A4C3A]" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="regions-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder={t('search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="ps-10"
            data-testid="regions-search"
          />
        </div>
        <Button 
          onClick={() => handleOpenDialog()}
          className="bg-[#0A4C3A] hover:bg-[#073B2D]"
          data-testid="add-region-button"
        >
          <Plus className="w-4 h-4 me-2" />
          {t('add_region')}
        </Button>
      </div>

      {/* Table */}
      <Card className="bg-white border border-gray-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-medium">{t('region_name_ar')}</TableHead>
                <TableHead className="font-medium">{t('region_name_en')}</TableHead>
                <TableHead className="font-medium">{t('status')}</TableHead>
                <TableHead className="font-medium text-center">{t('actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRegions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8 text-gray-500">
                    {t('no_data')}
                  </TableCell>
                </TableRow>
              ) : (
                filteredRegions.map((region) => (
                  <TableRow key={region.id} data-testid={`region-row-${region.id}`}>
                    <TableCell className="font-medium">{region.name_ar}</TableCell>
                    <TableCell>{region.name_en || '-'}</TableCell>
                    <TableCell>
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        region.is_active !== false
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {region.is_active !== false ? t('active') : t('inactive')}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(region)}
                          data-testid={`edit-region-${region.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setDeletingRegion(region);
                            setDeleteDialogOpen(true);
                          }}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-region-${region.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingRegion ? t('edit_region') : t('add_region')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name_ar">{t('region_name_ar')} *</Label>
              <Input
                id="name_ar"
                value={formData.name_ar}
                onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
                data-testid="region-name-ar"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name_en">{t('region_name_en')}</Label>
              <Input
                id="name_en"
                value={formData.name_en}
                onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
                dir="ltr"
                data-testid="region-name-en"
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="is_active">{t('active')}</Label>
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                data-testid="region-is-active"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {t('cancel')}
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-[#0A4C3A] hover:bg-[#073B2D]"
              data-testid="save-region-button"
            >
              {saving && <Loader2 className="w-4 h-4 me-2 animate-spin" />}
              {t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('confirm_delete')}</AlertDialogTitle>
            <AlertDialogDescription>
              {deletingRegion?.name_ar}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-region"
            >
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
